import {
  Avatar,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useBurgerMenuOffset, useIsDesktop } from 'Hooks';
import { SelfServiceReferralsToastCloseIcon } from 'Images';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';

export interface ISelfReferralToastAction {
  label: string;
  variant: 'contained' | 'outlined';
  onClick: () => void;
  startIconSrc?: string;
  endIconSrc?: string;
  ariaLabel?: string;
}

interface ISelfReferralToast {
  statusIconSrc: string;
  title: string;
  subtitle: string;
  actions: ISelfReferralToastAction[];
  onClose: () => void;
  isVisible?: boolean;
}

const TOAST_VIEWPORT_MARGIN_PX = 64;
const TOAST_DESKTOP_WIDTH_PX = 960;
/** Above footer/appBar (1100), below MUI drawer (1200) and modal (1300). */
const SELF_REFERRAL_TOAST_Z_INDEX = 1150;

const actionButtonSx = {
  fontSize: '18px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '& .MuiButton-startIcon': {
    marginInlineEnd: '8px',
    marginInlineStart: 0,
  },
  '& .MuiButton-endIcon': {
    marginInlineStart: '8px',
    marginInlineEnd: 0,
  },
};

const desktopActionButtonSx = {
  height: '44px',
  borderRadius: '24px',
  px: '24px',
  py: '10px',
  lineHeight: '22px',
};

const mobileActionButtonSx = {
  height: '42px',
  borderRadius: '20px',
  px: '12px',
  pt: '8px',
  pb: '10px',
  lineHeight: '24px',
  flex: 1,
  minWidth: 0,
};

export const SelfReferralToast = ({
  statusIconSrc,
  title,
  subtitle,
  actions,
  onClose,
  isVisible = true,
}: ISelfReferralToast) => {
  const isDesktop = useIsDesktop();
  const burgerMenuOffset = useBurgerMenuOffset();
  const closeLabel = Dictionary[DICTIONARY.General].close;
  const toastRightOffset = isDesktop
    ? TOAST_VIEWPORT_MARGIN_PX + burgerMenuOffset
    : 16;
  const [isAnimatedIn, setIsAnimatedIn] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setIsAnimatedIn(false);
      return undefined;
    }

    let enterFrame = 0;
    const startFrame = requestAnimationFrame(() => {
      enterFrame = requestAnimationFrame(() => setIsAnimatedIn(true));
    });

    return () => {
      cancelAnimationFrame(startFrame);
      cancelAnimationFrame(enterFrame);
    };
  }, [isVisible]);

  const isShown = isVisible && isAnimatedIn;
  const actionIconSize = isDesktop ? 20 : 16;

  return (
    <Paper
      role='status'
      aria-live='polite'
      elevation={0}
      sx={{
        position: 'fixed',
        bottom: isDesktop ? '64px' : '16px',
        right: `${toastRightOffset}px`,
        left: isDesktop ? 'auto' : '16px',
        zIndex: SELF_REFERRAL_TOAST_Z_INDEX,
        width: isDesktop
          ? `min(${TOAST_DESKTOP_WIDTH_PX}px, calc(100vw - ${TOAST_VIEWPORT_MARGIN_PX * 2 + burgerMenuOffset}px))`
          : 'auto',
        borderRadius: '16px',
        border: '2px solid',
        borderColor: 'primary.shade_200',
        bgcolor: 'secondary.light',
        boxShadow: '0px 2px 10px rgba(21, 82, 232, 0.12)',
        px: '16px',
        pt: isDesktop ? '24px' : '20px',
        pb: isDesktop ? '24px' : '16px',
        display: 'flex',
        flexDirection: isDesktop ? 'row' : 'column',
        alignItems: isDesktop ? 'center' : 'stretch',
        gap: '24px',
        direction: 'rtl',
        opacity: isShown ? 1 : 0,
        transform: isShown
          ? 'translateY(0) scale(1)'
          : 'translateY(calc(100% + 48px))',
        transition:
          'opacity 0.35s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), right 0.25s ease, width 0.25s ease',
        pointerEvents: 'auto',
      }}
    >
      <Stack
        direction='row'
        sx={{
          flex: 1,
          minWidth: 0,
          width: '100%',
          gap: isDesktop ? '24px' : '8px',
          alignItems: 'flex-start',
          pl: isDesktop ? 0 : '24px',
        }}
      >
        <Avatar
          src={statusIconSrc}
          alt=''
          aria-hidden='true'
          sx={{
            width: isDesktop ? 32 : 24,
            height: isDesktop ? 32 : 24,
            flexShrink: 0,
            mt: isDesktop ? 0 : '2px',
          }}
        />
        <Stack
          sx={{
            flex: 1,
            minWidth: 0,
            gap: isDesktop ? '8px' : '2px',
            alignItems: 'flex-start',
            textAlign: 'right',
          }}
        >
          <Typography
            variant={isDesktop ? 'body1' : 'body2'}
            sx={{
              color: 'secondary.main',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant='body3'
            sx={{
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {subtitle}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction='row'
        sx={{
          gap: '8px',
          flexShrink: 0,
          alignItems: 'center',
          width: isDesktop ? 'fit-content' : '100%',
        }}
      >
        {actions.map((action) => (
          <Button
            key={action.label}
            variant={action.variant}
            color='primary'
            onClick={action.onClick}
            aria-label={action.ariaLabel ?? action.label}
            startIcon={
              action.startIconSrc ? (
                <Avatar
                  src={action.startIconSrc}
                  alt=''
                  aria-hidden='true'
                  sx={{ width: actionIconSize, height: actionIconSize }}
                />
              ) : undefined
            }
            endIcon={
              action.endIconSrc ? (
                <Avatar
                  src={action.endIconSrc}
                  alt=''
                  aria-hidden='true'
                  sx={{
                    width: actionIconSize,
                    height: actionIconSize,
                    bgcolor: 'transparent',
                  }}
                />
              ) : undefined
            }
            sx={{
              ...actionButtonSx,
              ...(isDesktop
                ? {
                    ...desktopActionButtonSx,
                    minWidth:
                      action.variant === 'contained' ? '220px' : '160px',
                  }
                : mobileActionButtonSx),
            }}
          >
            {action.label}
          </Button>
        ))}
      </Stack>

      <IconButton
        onClick={onClose}
        aria-label={closeLabel}
        sx={{
          flexShrink: 0,
          p: 0,
          position: isDesktop ? 'relative' : 'absolute',
          top: isDesktop ? undefined : '8px',
          left: isDesktop ? undefined : '8px',
          width: isDesktop ? 40 : 32,
          height: isDesktop ? 40 : 32,
          borderRadius: '32px',
        }}
      >
        <Avatar
          src={SelfServiceReferralsToastCloseIcon}
          alt=''
          sx={{
            width: 14,
            height: 14,
            borderRadius: 0,
            bgcolor: 'transparent',
          }}
        />
      </IconButton>
    </Paper>
  );
};
